import React, { useEffect } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { QueryParamProvider } from 'use-query-params';
import { ReactRouter6Adapter } from 'use-query-params/adapters/react-router-6';
import _get from 'lodash/get';
import _without from 'lodash/without';
import { UserRoleEnum } from 'graphql-common';
import { APP_URLS } from 'constants/urls';
import { ToastContainer } from '@lib/components/Toast/Toast';
import {
  GetUrlParams,
  PATH_AUTH,
  PATH_ERROR,
  PATH_SETUP,
} from '@lib/enums/urls';
import LogoLoader from '@lib/components/LogoLoader/LogoLoader';
import isMobile from '@lib/utils/isMobile';
import useWhoamiQueryHook from 'utils/fetch/useWhoamiQueryHook';
import { stringifyWithPrefix } from '@lib/utils/url';
import { setSentryTags } from '@lib/utils/sentryHelpers';
import UpdateHtmlLang from '@lib/components/UpdateHtmlLang/UpdateHtmlLang';
import ErrorPage from '@lib/components/ErrorPage/ErrorPage';
import getDescriptionFromError from '@lib/utils/getDescriptionFromError';
import { ErrorPageStatusEnum } from '@lib/components/ErrorPage/enums';
import { useSessionStorage } from 'usehooks-ts';
import { ANIMATION_LOADER_AVAILABLE } from '@lib/enums/localStorageKeys';
import CircleLoader from '@lib/components/CircleLoader/CircleLoader';
import useTechnicalPause from '@lib/hooks/useTechnicalPause';
import TechnicalPauseContext from '@lib/contexts/TechnicalPauseContext';
import TechnicalPauseTracker from '@lib/components/TechnicalPauseTracker/TechnicalPauseTracker';
import ReleaseTrackerProvider from '@lib/components/ReleaseTracker/ReleaseTrackerProvider';
import { captureException } from 'utils/captureException';

const {
  MODE,
  VITE_ADMIN_PLATFORM_URL: ADMIN_PLATFORM,
  VITE_USER_PLATFORM_URL: USER_PLATFORM,
} = import.meta.env;

function ProtectedRouteContent() {
  const { pathname, search } = useLocation();
  const urlSearchParams = new URLSearchParams(search);
  const dst = urlSearchParams.get(GetUrlParams.Dst) as string;
  const isDst = dst && dst !== '/';
  const pathnameArr = _without(pathname.split('/'), '');
  const pathBase = pathnameArr.length ? pathnameArr[0] : '/';

  const technicalPauseResult = useTechnicalPause(captureException);
  const { activeTechnicalPause, fetched: technicalPauseFetched } =
    technicalPauseResult;

  const [showLogo, setShowLogo] = useSessionStorage(
    ANIMATION_LOADER_AVAILABLE,
    true,
  );
  const isSsoLogin = urlSearchParams.get('type') === 'sso';
  const shouldShowLogo =
    showLogo && pathname !== `/${PATH_AUTH}` && !isDst && !isSsoLogin;

  // Whoami
  const {
    data,
    loading,
    error,
    refetch: refetchWhoami,
    firstLoading,
  } = useWhoamiQueryHook();
  const { phone, tablet } = isMobile();
  const isMobileDevice = phone || tablet;
  const userId = _get(data, 'whoami.resource.id');
  const userEmail = _get(data, 'whoami.resource.email');
  const username = _get(data, 'whoami.resource.username');
  // const deactivatedAt = _get(data, 'whoami.resource.deactivatedAt');
  const userType = _get(data, 'whoami.resource.__typename');
  const passwordSetupRequired = _get(
    data,
    'whoami.resource.passwordSetupRequired',
  );
  const role = _get(data, 'whoami.resource.role');
  const graphQLErrors = _get(error, 'graphQLErrors', []);
  const authError = graphQLErrors.find(
    ({ extensions }) => extensions?.code === 'AUTHENTICATION_ERROR',
  );

  const isUserAuth = !authError && !!userId;
  const isAdminBased = role && UserRoleEnum.FullAdmin === role;
  const defaultUrlParamsAfterAuth = isDst ? { dst } : undefined;

  let defaultUrlAfterAuth = USER_PLATFORM;
  if (isDst && dst.indexOf(ADMIN_PLATFORM) === 0) {
    defaultUrlAfterAuth = ADMIN_PLATFORM;
  } else if (isDst && dst.indexOf(USER_PLATFORM) === 0) {
    defaultUrlAfterAuth = USER_PLATFORM;
  } else if (isAdminBased && !isDst) {
    defaultUrlAfterAuth = isMobileDevice ? USER_PLATFORM : ADMIN_PLATFORM;
  }
  defaultUrlAfterAuth = `${defaultUrlAfterAuth}/auth`;

  if (MODE !== 'development' && !loading) {
    setSentryTags({ isUserAuth, userId, userEmail, userType, username });
  }

  useEffect(() => {
    refetchWhoami();
  }, [pathBase, refetchWhoami]);

  useEffect(() => {
    if (firstLoading && shouldShowLogo) {
      const timer = setTimeout(() => {
        setShowLogo(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [firstLoading, setShowLogo, shouldShowLogo]);

  useEffect(() => {
    const handleBeforeUnload = () => {
      sessionStorage.setItem(ANIMATION_LOADER_AVAILABLE, 'true');
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [setShowLogo]);

  if (shouldShowLogo) {
    return <LogoLoader setVisible={setShowLogo} />;
  }
  if (firstLoading) {
    return <CircleLoader />;
  }

  const appRender = () => (
    <TechnicalPauseContext.Provider value={technicalPauseResult}>
      <QueryParamProvider adapter={ReactRouter6Adapter}>
        <UpdateHtmlLang />
        <Outlet />
        <ToastContainer />
      </QueryParamProvider>
    </TechnicalPauseContext.Provider>
  );

  const appNavigate = (to: string, external?: boolean, params?: unknown) => {
    if (external) {
      let url = to;
      if (params) {
        const searchStr = stringifyWithPrefix(params);
        url = `${to}${searchStr}`;
      }
      window.location.href = url;
      return undefined;
    }
    return <Navigate to={to} replace />;
  };

  // Wait for CDN check before showing error — avoids race condition
  if (error && !authError && !technicalPauseFetched) {
    return <CircleLoader />;
  }
  if (activeTechnicalPause) {
    return (
      <TechnicalPauseTracker
        technicalPauseEndDate={activeTechnicalPause.endAt}
      />
    );
  }

  switch (pathBase) {
    case APP_URLS.root.path:
      if (isUserAuth) {
        return appNavigate(
          defaultUrlAfterAuth,
          true,
          defaultUrlParamsAfterAuth,
        );
      }
      if (error && !authError) {
        return <Navigate to={APP_URLS.error.path} />;
      }
      return appNavigate(APP_URLS.auth.login.path);
    case PATH_ERROR:
      if (pathBase === PATH_ERROR && authError) {
        return appNavigate(APP_URLS.auth.login.path);
      }
      return (
        <ErrorPage
          status={ErrorPageStatusEnum.Status500}
          description={getDescriptionFromError(error)}
          logoUrl={APP_URLS.root.path}
          fullHeight
        />
      );
    case PATH_AUTH:
      if (error && !authError) {
        return <Navigate to={APP_URLS.error.path} />;
      }
      if (pathname !== APP_URLS.auth.resetPassword.path) {
        if (!passwordSetupRequired && isUserAuth) {
          return appNavigate(
            defaultUrlAfterAuth,
            true,
            defaultUrlParamsAfterAuth,
          );
        }
        if (passwordSetupRequired && isUserAuth) {
          return appNavigate(APP_URLS.setup.updatePassword.path);
        }
      }
      return appRender();
    case PATH_SETUP:
      if (error && !authError) {
        return <Navigate to={APP_URLS.error.path} />;
      }
      if (pathname.indexOf(APP_URLS.setup.finishRegistration.path) > -1) {
        return appRender();
      }
      if (pathname.indexOf(APP_URLS.setup.confirmEmail.path) > -1) {
        return appRender();
      }
      if (!passwordSetupRequired && isUserAuth) {
        return appNavigate(
          defaultUrlAfterAuth,
          true,
          defaultUrlParamsAfterAuth,
        );
      }
      if (error && !authError) {
        return <Navigate to={APP_URLS.error.path} />;
      }
      if (
        !isUserAuth &&
        pathname.indexOf(APP_URLS.setup.updatePassword.path) > -1
      ) {
        return appNavigate(APP_URLS.auth.login.path);
      }
      return appRender();
    default:
      return appRender();
  }
}

function ProtectedRoute() {
  return (
    <ReleaseTrackerProvider captureException={captureException}>
      <ProtectedRouteContent />
    </ReleaseTrackerProvider>
  );
}

export default ProtectedRoute;
